import { useMutation, useQuery } from '@apollo/client'
import { TitledPaper, TypedFormik } from '@dai/components'
import { Check, Close } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { logAnalyticsEvent } from 'api/Firebase'
import { uniqBy } from 'lodash'
import React, { useEffect } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'

import CenteredBox from '@/components/CenteredBox'
import {
  GET_ALL_TENANT_STORES,
  GetAllTenantStoresQuery,
  GetAllTenantStoresQueryVariables,
  StoreFragment,
  UPDATE_USER,
  UpdateUserMutation,
  UpdateUserMutationVariables,
} from '@/graphql'
import useT from '@/i18n/useT'
import AddEditStoreDialog from '@/pages/forms/store/AddEditStoreDialog'
import AddEditStoreForm from '@/pages/forms/store/AddEditStoreForm'
import { useCreateEditStore } from '@/pages/forms/store/AddEditStoreShared'

import { AccountSetUpStepProps } from '../types'

const StoreListSkeleton = ({
  count = 5,
  selectable,
}: {
  count?: number
  selectable: boolean
}) => {
  return (
    <Box
      p={2}
      sx={{
        border: '1px solid lightGrey',
        borderRadius: 1,
        overflow: 'hidden',
        height: 400,
      }}
    >
      <Stack spacing={2}>
        {Array.from(new Array(count)).map((_, index) => (
          <Stack
            key={index}
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{
              p: 2,
              borderRadius: 1,
            }}
          >
            {selectable && (
              <Skeleton variant="rectangular" width={24} height={24} />
            )}
            <Stack spacing={0.5} sx={{ flex: 1 }}>
              <Skeleton variant="text" width="70%" height={24} />
              <Skeleton variant="text" width="90%" height={20} />
              <Skeleton variant="text" width="50%" height={20} />
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Box>
  )
}

const STORES_PER_PAGE = 20

function FindAndCreateStores({
  selectable = false,
  refetchStores,
  nextStep,
  user,
}: {
  selectable?: boolean
  refetchStores?: () => void
} & AccountSetUpStepProps) {
  const { t } = useT()
  const [selectedStores, setSelectedStores] = React.useState<string[]>([])
  useEffect(() => {
    setSelectedStores(user?.stores.map(store => store.id) || [])
  }, [user?.email]) // eslint-disable-line react-hooks/exhaustive-deps

  const [open, setOpen] = React.useState(false)
  const [dialogMode, setDialogMode] = React.useState<'add' | 'edit'>('add')
  const [selectedStore, setSelectedStore] =
    React.useState<StoreFragment | null>(null)

  const handleAddNewStore = () => {
    setDialogMode('add')
    setSelectedStore(null)
    setOpen(true)
  }

  const handleEditStore = (store: StoreFragment) => {
    setDialogMode('edit')
    setSelectedStore(store)
    setOpen(true)
  }

  const handleCloseDialog = (store?: StoreFragment | null) => {
    setOpen(false)
    if (store) {
      if (dialogMode === 'add') {
        setSelectedStores(prev => [...prev, store.id])
        stores.updateQuery(prev => ({
          ...prev,
          allTenantStores: {
            ...prev.allTenantStores,
            totalCount: prev.allTenantStores.totalCount + 1,
            results: [...prev.allTenantStores.results, store],
          },
        }))
      } else {
        stores.updateQuery(prev => ({
          ...prev,
          allTenantStores: {
            ...prev.allTenantStores,
            results: prev.allTenantStores.results.map(s =>
              s.id === store.id ? store : s,
            ),
          },
        }))
      }
    }
  }

  const { onSubmit, saving } = useCreateEditStore({
    onSuccess: store => {
      refetchStores?.()
      if (store) {
        setSelectedStores(prev =>
          prev.includes(store.id) ? prev : [...prev, store.id],
        )
        stores.updateQuery(prev => {
          const existingIndex = prev.allTenantStores.results.findIndex(
            ({ id }) => id === store.id,
          )
          let newResults: StoreFragment[]

          if (existingIndex >= 0) {
            newResults = [...prev.allTenantStores.results]
            newResults[existingIndex] = store
          } else {
            newResults = [...prev.allTenantStores.results, store]
          }

          return {
            ...prev,
            allTenantStores: {
              ...prev.allTenantStores,
              totalCount:
                existingIndex >= 0
                  ? prev.allTenantStores.totalCount
                  : prev.allTenantStores.totalCount + 1,
              results: newResults,
            },
          }
        })
      }
    },
  })

  const stores = useQuery<
    GetAllTenantStoresQuery,
    GetAllTenantStoresQueryVariables
  >(GET_ALL_TENANT_STORES, {
    variables: {
      pagination: {
        offset: 0,
        limit: STORES_PER_PAGE,
      },
    },
    fetchPolicy: 'cache-and-network',
  })
  const hasActiveStores = React.useMemo(() => {
    if (!stores.data?.allTenantStores.results) return false
    return stores.data.allTenantStores.results.some(store => !store.inactiveAt)
  }, [stores.data?.allTenantStores.results])
  const numStores = stores.data?.allTenantStores.results?.length || 0

  const [updateUser] = useMutation<
    UpdateUserMutation,
    UpdateUserMutationVariables
  >(UPDATE_USER)

  const handleScroll = React.useCallback(() => {
    if (!stores.data) return
    const currentLength = stores.data.allTenantStores.results.length
    const totalCount = stores.data.allTenantStores.totalCount

    if (currentLength < totalCount) {
      stores.fetchMore({
        variables: {
          pagination: {
            offset: currentLength,
            limit: STORES_PER_PAGE,
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev

          return {
            ...fetchMoreResult,
            allTenantStores: {
              ...fetchMoreResult.allTenantStores,
              results: uniqBy(
                [
                  ...prev.allTenantStores.results,
                  ...fetchMoreResult.allTenantStores.results,
                ],
                'id',
              ),
            },
          }
        },
      })
    }
  }, [stores])

  if (!stores.loading && !stores.data?.allTenantStores.results.length) {
    return (
      <TitledPaper
        title={t('createStore.createStore.title')}
        subtitle={t('createStore.createStore.subtitle')}
      >
        <AddEditStoreForm mode="add" onSubmit={onSubmit} saving={saving} />
      </TitledPaper>
    )
  }
  return (
    <TitledPaper
      title={
        selectable
          ? t('createStore.find.title')
          : t('createStore.create.multi.title')
      }
      subtitle={
        selectable
          ? t('createStore.find.subtitle')
          : t('createStore.create.multi.subtitle')
      }
    >
      <TypedFormik
        initialValues={{}}
        onSubmit={async (_, { setStatus }) => {
          try {
            if (selectable) {
              if (!user) {
                throw Error(t('createStore.error.sessionExpired'))
              }
              await updateUser({
                variables: {
                  data: {
                    id: user.id,
                    storeIds: selectedStores,
                  },
                },
              })
            }

            setStatus({ success: true })
            logAnalyticsEvent('set_up_stores_complete')
            nextStep()
          } catch (error: any) {
            const message = error.message || t('common.somethingWentWrong')
            setStatus({ success: false, message })
          }
        }}
      >
        {({ status, handleSubmit, isSubmitting }) => (
          <form noValidate onSubmit={handleSubmit}>
            <Stack spacing={4}>
              {status?.message && (
                <Alert severity={status.success ? 'success' : 'warning'}>
                  {status.message}
                </Alert>
              )}
              <Stack spacing={2}>
                {stores.loading ? (
                  <StoreListSkeleton selectable={selectable} />
                ) : (
                  <InfiniteScroll
                    next={handleScroll}
                    hasMore={
                      (stores.data?.allTenantStores.results.length || 0) <
                      (stores.data?.allTenantStores.totalCount || 0)
                    }
                    loader={
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          p: 2,
                        }}
                      >
                        <CircularProgress size={24} />
                      </Box>
                    }
                    dataLength={
                      stores.data?.allTenantStores.results.length || 0
                    }
                    height={400}
                    style={{
                      border:
                        stores.data?.allTenantStores.results.length &&
                        '1px solid lightGrey',
                      borderRadius: 6,
                    }}
                  >
                    <Box p={2}>
                      {stores.loading ? (
                        <CenteredBox>
                          <CircularProgress />
                        </CenteredBox>
                      ) : stores.error ? (
                        <CenteredBox>
                          <Alert severity="error">{stores.error.message}</Alert>
                        </CenteredBox>
                      ) : !stores.data?.allTenantStores.results.length ? (
                        <CenteredBox>
                          <Alert severity="warning">
                            {t('createStore.noStores')}
                          </Alert>
                        </CenteredBox>
                      ) : (
                        <Stack spacing={2}>
                          {stores.data?.allTenantStores.results.map(store => (
                            <Stack
                              key={store.id}
                              direction="row"
                              alignItems="center"
                              spacing={2}
                              onClick={() =>
                                !selectable && handleEditStore(store)
                              }
                              sx={{
                                p: 2,
                                borderRadius: 1,
                                '&:hover': {
                                  bgcolor: 'action.hover',
                                },
                                cursor: !selectable ? 'pointer' : null,
                              }}
                            >
                              {selectable && (
                                <Checkbox
                                  checked={selectedStores.includes(store.id)}
                                  onChange={() => {
                                    setSelectedStores(
                                      selectedStores.includes(store.id)
                                        ? selectedStores.filter(
                                            id => id !== store.id,
                                          )
                                        : [...selectedStores, store.id],
                                    )
                                  }}
                                />
                              )}
                              <Stack spacing={0.5} sx={{ flex: 1 }}>
                                <Typography variant="body1">
                                  {store.location?.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                >
                                  {store.location?.address1}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                >
                                  {`${store.location?.city}, ${store.location?.state} ${store.location?.postalCode}`}
                                </Typography>
                              </Stack>
                              <Tooltip
                                title={t('addEditStore.shared.activeTooltip')}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    color="textSecondary"
                                  >
                                    {!store.inactiveAt
                                      ? t('common.status.active')
                                      : t('common.status.inactive')}
                                  </Typography>
                                  {!store.inactiveAt ? <Check /> : <Close />}
                                </Box>
                              </Tooltip>
                            </Stack>
                          ))}
                        </Stack>
                      )}
                    </Box>
                  </InfiniteScroll>
                )}
                {selectable && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {t('createStore.selectedCount', {
                        selected: selectedStores.length,
                        total: stores.data?.allTenantStores?.totalCount || 0,
                      })}
                    </Typography>
                  </Box>
                )}
                {!selectable && numStores > 0 && !hasActiveStores && (
                  <Alert severity="warning">
                    You need at least one active store to continue
                  </Alert>
                )}
              </Stack>
              <Stack direction="row" spacing={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="primary"
                  disabled={isSubmitting || stores.loading}
                  onClick={() => {
                    handleAddNewStore()
                  }}
                >
                  {t('createStore.addNew')}
                </Button>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  loading={isSubmitting}
                  loadingPosition="end"
                  disabled={
                    (selectable && selectedStores.length === 0) ||
                    (!selectable && !hasActiveStores) ||
                    stores.loading
                  }
                >
                  {t('common.continue')}
                </Button>
              </Stack>
            </Stack>
          </form>
        )}
      </TypedFormik>
      {dialogMode === 'edit' && selectedStore ? (
        <AddEditStoreDialog
          mode={'edit'}
          store={selectedStore}
          open={open}
          onSubmit={onSubmit}
          saving={saving}
          onClose={store => {
            handleCloseDialog(store)
          }}
        />
      ) : (
        <AddEditStoreDialog
          mode={'add'}
          open={open}
          onSubmit={onSubmit}
          saving={saving}
          onClose={store => {
            handleCloseDialog(store)
          }}
        />
      )}
    </TitledPaper>
  )
}

export default FindAndCreateStores
